import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useLayoutEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import RightPanel, {
  useCurrentRightPanelName,
  useRightPanel,
} from '../app/layout/RightPanel';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import { useAppDispatch } from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import { gql } from '../graphql/generated/gql';
import PostRow from '../post/PostRow';
import PostRowMeta from '../post/PostRowMeta';
import PostSectionHeader from '../post/PostSectionHeader';
import { UIActions } from '../ui/uiSlice';
import ReferralModal from '../user/ReferralModal';
import { isFeatureEnabled } from '../utils/features';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import { openPricingModal } from '../utils/url/pricingPath';
import HomeActivities from './HomeActivities';
import HomeActivitiesButton from './HomeActivitiesButton';
import * as styles from './HomeView.module.scss';
import JulyProductUpdate, {
  shouldShowProductUpdate,
} from './JulyProductUpdate';
import OrganizationSummary from './organizationSummary/OrganizationSummary';

const ORGANIZATION_SUMMARY_QUERY = gql(/** @complexity 2 */ `
  query organizationSummary {
    session: currentSession {
      organization {
        id
        summary
      }
    }
  }
`);

const VIEWED_POSTS_QUERY = gql(/** @complexity 4 */ `
  query viewedPosts($afterCursor: String) {
    listPosts(first: 25, after: $afterCursor, filters: [VIEWER], sortBy: VIEWED) {
      edges {
        node {
          id
          ...PostRow_post
        }
        viewedAt
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

type HomeModal = 'pricing' | 'referral';

interface HomeViewProps {
  openModal?: HomeModal;
}

const useModal = (modal?: HomeModal) => {
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (modal === 'pricing') {
      openPricingModal(dispatch);
    } else if (modal === 'referral') {
      dispatch(UIActions.openModal(ReferralModal));
    } else {
      return undefined;
    }

    return () => {
      dispatch(UIActions.closeModal());
    };
  }, [dispatch, modal]);
};

const RIGHT_PANEL_NAME = 'homeActivities';

const HomeView = ({ openModal }: HomeViewProps) => {
  const { data, loading } = useQuery(ORGANIZATION_SUMMARY_QUERY);
  const organization = data?.session?.organization;
  const summary = useMemo(() => {
    if (organization?.summary) {
      return JSON.parse(organization.summary) as Op[];
    }
    return null;
  }, [organization]);

  const postsResult = useQuery(VIEWED_POSTS_QUERY);

  const fetchMore = ConnectionHelper.generateFetchMore(
    VIEWED_POSTS_QUERY,
    postsResult,
    'listPosts',
  );
  const postEdges = _.compact(
    postsResult.data?.listPosts?.edges?.map((edge) =>
      edge?.node ? { viewedAt: edge.viewedAt, node: edge.node } : null,
    ),
  );

  useModal(openModal);

  const currentRightPanelName = useCurrentRightPanelName();
  const { openRightPanel, closeRightPanel } = useRightPanel();

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <TwoColumnRow
        onIsAsideShownChange={(isAsideShown) => {
          if (isAsideShown && currentRightPanelName === RIGHT_PANEL_NAME) {
            closeRightPanel();
          }
        }}
        main={(isAsideShown) => (
          <div className={styles.main}>
            <OrganizationSummary
              summary={summary}
              onActivityOpen={
                isAsideShown
                  ? undefined
                  : () => {
                      openRightPanel(
                        <RightPanel.Inner
                          header={
                            <RightPanel.Header
                              title="Activity"
                              controls={
                                <HomeActivitiesButton popoverPlacement="bottom-start" />
                              }
                            />
                          }
                        >
                          <HomeActivities />
                        </RightPanel.Inner>,
                        { name: RIGHT_PANEL_NAME },
                      );
                    }
              }
            />
            {loading && !summary ? undefined : (
              <>
                <PostSectionHeader title="Viewed" />
                {!postsResult.loading && postEdges.length === 0 ? (
                  <EmptyState
                    imageName="posts"
                    title="Haven't read anything yet."
                    message="Browse around. There's something interesting in here somewhere."
                  />
                ) : (
                  <div role="feed" aria-label="Recently viewed posts">
                    <PostRow.Provider>
                      {postEdges.map(({ node: post, viewedAt }) => (
                        <PostRow
                          key={post.id}
                          post={post}
                          meta={
                            <PostRowMeta
                              attribute="userViewedAt"
                              values={{ userViewedAt: viewedAt }}
                            />
                          }
                        />
                      ))}
                    </PostRow.Provider>
                    <InfiniteScrollWaypoint fetchMore={fetchMore} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        aside={
          <div className={styles.activities}>
            {shouldShowProductUpdate && (
              <>
                <h4 className={styles.asideHeader}>New in Slab</h4>
                <JulyProductUpdate />
              </>
            )}
            <div
              className={classNames(styles.asideHeader, {
                [styles.isHomeActivitiesV2]:
                  isFeatureEnabled('home_activity_v2'),
              })}
            >
              <h4>Activity</h4>
              <HomeActivitiesButton popoverPlacement="bottom-end" />
            </div>
            <div className={styles.asideActivitiesContainer}>
              <HomeActivities />
            </div>
          </div>
        }
      />
    </PageLayout.Content>
  );
};

export default HomeView;
